import React, { useState, useEffect, useRef } from 'react';
import { Users, Smile, Reply, MoreVertical, Search } from 'lucide-react';
import { useStreamChat } from '../../../context/StreamChatContext';
import CustomMessageItem from './CustomMessageItem';
import styles from '../Chat.module.css';

const REACTION_OPTIONS = [
  { type: 'like', emoji: '👍', label: 'Like' },
  { type: 'love', emoji: '❤️', label: 'Love' },
  { type: 'haha', emoji: '😂', label: 'Laugh' },
  { type: 'wow', emoji: '😲', label: 'Wow' },
  { type: 'sad', emoji: '😔', label: 'Sad' },
];

const formatDateDivider = (date) => {
  const messageDate = new Date(date);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  today.setHours(0, 0, 0, 0);
  yesterday.setHours(0, 0, 0, 0);
  messageDate.setHours(0, 0, 0, 0);

  if (messageDate.getTime() === today.getTime()) return 'Today';
  if (messageDate.getTime() === yesterday.getTime()) return 'Yesterday';

  const options = { weekday: 'long', month: 'long', day: 'numeric' };
  if (messageDate.getFullYear() !== today.getFullYear()) options.year = 'numeric';
  return messageDate.toLocaleDateString('en-US', options);
};

const CustomMessageList = ({ channel, searchResults, isSearching, searchQuery, onClearSearch }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [typingUsers, setTypingUsers] = useState([]);
  const [hoveredMessageId, setHoveredMessageId] = useState(null);
  const [reactionPickerMessageId, setReactionPickerMessageId] = useState(null);
  const messagesEndRef = useRef(null);
  const messageContainerRef = useRef(null);
  const currentUser = window.streamChatClient?.user;
  const { fetchUnreadCount } = useStreamChat();

  useEffect(() => {
    if (!channel) return;

    setIsInitialLoad(true);

    const loadMessages = async () => {
      try {
        setLoading(true);
        const response = await channel.query({ messages: { limit: 50 } });
        setMessages(response.messages || []);

        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
          setIsInitialLoad(false);
        }, 100);

        try {
          await channel.markRead();
          if (fetchUnreadCount) setTimeout(fetchUnreadCount, 100);
        } catch (markReadError) {
          console.error('[CustomMessageList] Error marking channel as read:', markReadError);
        }
      } catch (error) {
        console.error('Error loading messages:', error);
      } finally {
        setLoading(false);
      }
    };

    loadMessages();

    const handleNewMessage = async (event) => {
      setMessages(prev => [...prev, event.message]);
      try {
        await channel.markRead();
        if (fetchUnreadCount) setTimeout(fetchUnreadCount, 100);
      } catch (error) {
        console.error('[CustomMessageList] Error marking new message as read:', error);
      }
    };

    const handleMessageUpdated = (event) => {
      setMessages(prev => prev.map(msg => msg.id === event.message.id ? event.message : msg));
    };

    const handleReactionChanged = (event) => {
      if (!event.message?.id) return;
      setMessages(prev => prev.map(msg => msg.id === event.message.id ? event.message : msg));
    };

    const handleMessageDeleted = (event) => {
      setMessages(prev => prev.filter(msg => msg.id !== event.message.id));
    };

    const handleTypingStart = (event) => {
      if (event.user?.id !== currentUser?.id) {
        setTypingUsers(prev => {
          const filtered = prev.filter(u => u.id !== event.user.id);
          return [...filtered, event.user];
        });
      }
    };

    const handleTypingStop = (event) => {
      if (event.user?.id !== currentUser?.id) {
        setTypingUsers(prev => prev.filter(u => u.id !== event.user.id));
      }
    };

    channel.on('message.new', handleNewMessage);
    channel.on('message.updated', handleMessageUpdated);
    channel.on('message.deleted', handleMessageDeleted);
    channel.on('reaction.new', handleReactionChanged);
    channel.on('reaction.updated', handleReactionChanged);
    channel.on('reaction.deleted', handleReactionChanged);
    channel.on('typing.start', handleTypingStart);
    channel.on('typing.stop', handleTypingStop);

    return () => {
      channel.off('message.new', handleNewMessage);
      channel.off('message.updated', handleMessageUpdated);
      channel.off('message.deleted', handleMessageDeleted);
      channel.off('reaction.new', handleReactionChanged);
      channel.off('reaction.updated', handleReactionChanged);
      channel.off('reaction.deleted', handleReactionChanged);
      channel.off('typing.start', handleTypingStart);
      channel.off('typing.stop', handleTypingStop);
    };
  }, [channel]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: isInitialLoad ? 'auto' : 'smooth' });

    if (channel && messages.length > 0) {
      const markAsReadDebounced = setTimeout(async () => {
        try {
          await channel.markRead();
          if (fetchUnreadCount) fetchUnreadCount();
        } catch (error) {
          console.error('[CustomMessageList] Error marking messages as read on scroll:', error);
        }
      }, 500);
      return () => clearTimeout(markAsReadDebounced);
    }
  }, [messages, channel, fetchUnreadCount, isInitialLoad]);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!document.hidden && channel) {
        try {
          await channel.markRead();
          if (fetchUnreadCount) fetchUnreadCount();
        } catch (error) {
          console.error('[CustomMessageList] Error marking as read on visibility change:', error);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [channel, fetchUnreadCount]);

  const refreshMessages = async () => {
    if (!channel) return;
    const response = await channel.query({ messages: { limit: 50 } });
    setMessages(response.messages || []);
  };

  const handleToggleReaction = async (message, type) => {
    if (!channel || !message?.id || !type) return;
    const alreadyReacted = (message.own_reactions || []).some(
      (reaction) => reaction.type === type && (!currentUser?.id || reaction.user_id === currentUser.id || reaction.user?.id === currentUser.id),
    );

    try {
      const response = alreadyReacted
        ? await channel.deleteReaction(message.id, type)
        : await channel.sendReaction(message.id, { type });

      if (response?.message) {
        setMessages(prev => prev.map(msg => msg.id === message.id ? response.message : msg));
      } else {
        await refreshMessages();
      }
    } catch (error) {
      console.error('[CustomMessageList] Error toggling reaction:', error);
    } finally {
      setReactionPickerMessageId(null);
    }
  };

  const groupMessagesByDate = (messages) => {
    const groups = [];
    let currentDate = null;

    messages.forEach((message) => {
      const messageDate = formatDateDivider(message.created_at);
      if (messageDate !== currentDate) {
        currentDate = messageDate;
        groups.push({ type: 'date-divider', date: messageDate, id: `divider-${message.id}` });
      }
      groups.push({ type: 'message', message });
    });

    return groups;
  };

  if (loading) {
    return (
      <div className={styles.messageArea} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div className={styles.spinner} style={{ margin: '0 auto var(--sp-4)' }} />
          <p style={{ color: 'var(--lodge-onyx-400)', fontSize: 14 }}>Loading messages...</p>
        </div>
      </div>
    );
  }

  const messageGroups = groupMessagesByDate(messages);

  // Show search results if searching
  if (searchQuery && (isSearching || searchResults)) {
    return (
      <div className={styles.messageArea} ref={messageContainerRef}>
        {isSearching ? (
          <div className={styles.emptyState}>
            <div className={styles.spinner} />
            <p style={{ marginTop: 'var(--sp-4)', color: 'var(--lodge-onyx-400)', fontSize: 13 }}>Searching messages...</p>
          </div>
        ) : searchResults && searchResults.length > 0 ? (
          <div>
            <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--lodge-onyx-400)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 'var(--sp-4)' }}>
              {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{searchQuery}"
            </p>
            {searchResults.map((result, index) => (
              <div
                key={index}
                onClick={() => onClearSearch?.()}
                style={{
                  background: 'var(--lodge-white)',
                  borderRadius: 'var(--radius-lg)',
                  padding: 'var(--sp-4)',
                  marginBottom: 'var(--sp-3)',
                  border: '1px solid var(--lodge-onyx-100)',
                  boxShadow: 'var(--shadow-xs)',
                  cursor: 'pointer',
                  transition: 'background var(--duration-base) var(--easing-standard)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--sp-3)' }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 'var(--radius-md)',
                    background: 'var(--lodge-orange-400)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontWeight: 600, fontSize: 12, flexShrink: 0,
                  }}>
                    {result.message?.user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', marginBottom: 2 }}>
                      <span style={{ fontWeight: 500, color: 'var(--lodge-onyx-800)', fontSize: 13 }}>
                        {result.message?.user?.name || 'Unknown User'}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--lodge-onyx-300)' }}>
                        {new Date(result.message?.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p style={{ color: 'var(--lodge-onyx-700)', fontSize: 13, lineHeight: 1.5 }}>
                      {result.message?.text}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}><Search size={28} /></div>
            <h3 className={styles.emptyTitle}>No results found</h3>
            <p className={styles.emptyText}>No messages found matching "{searchQuery}"</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.messageArea} ref={messageContainerRef}>
      {messages.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}><Users size={28} /></div>
          <h3 className={styles.emptyTitle}>Start the conversation</h3>
          <p className={styles.emptyText}>Send a message to begin chatting with your team</p>
        </div>
      ) : (
        <>
          {messageGroups.map((item) => {
            if (item.type === 'date-divider') {
              return (
                <div key={item.id} className={styles.dateDivider}>
                  <div className={styles.dateDividerLine} />
                  <span className={styles.dateDividerLabel}>{item.date}</span>
                  <div className={styles.dateDividerLine} />
                </div>
              );
            }

            return (
              <div
                key={item.message.id}
                onMouseEnter={() => setHoveredMessageId(item.message.id)}
                onMouseLeave={() => {
                  setHoveredMessageId(null);
                  setReactionPickerMessageId(null);
                }}
                className={`${styles.messageRow} ${item.message.user?.id === currentUser?.id ? styles.own : ''}`}
              >
                <CustomMessageItem
                  message={item.message}
                  isOwn={item.message.user?.id === currentUser?.id}
                  channel={channel}
                  showActions={hoveredMessageId === item.message.id}
                  currentUserId={currentUser?.id}
                  onToggleReaction={handleToggleReaction}
                />

                {hoveredMessageId === item.message.id && (
                  <div className={styles.messageActions}>
                    <button
                      className={styles.messageActionBtn}
                      title="Add reaction"
                      onClick={() => setReactionPickerMessageId((prev) => prev === item.message.id ? null : item.message.id)}
                    >
                      <Smile size={14} />
                    </button>
                    {reactionPickerMessageId === item.message.id && (
                      <div className={styles.reactionPicker}>
                        {REACTION_OPTIONS.map((reaction) => (
                          <button
                            key={reaction.type}
                            type="button"
                            className={styles.reactionPickerBtn}
                            onClick={() => handleToggleReaction(item.message, reaction.type)}
                            title={reaction.label}
                          >
                            {reaction.emoji}
                          </button>
                        ))}
                      </div>
                    )}
                    <button className={styles.messageActionBtn} title="Reply">
                      <Reply size={14} />
                    </button>
                    <button className={styles.messageActionBtn} title="More">
                      <MoreVertical size={14} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {typingUsers.length > 0 && (
            <div className={styles.typingIndicator}>
              <span>
                {typingUsers.map(u => u.name || u.id).join(', ')}
                {typingUsers.length === 1 ? ' is' : ' are'} typing
              </span>
              <div className={styles.typingDots}>
                <span className={styles.typingDot} />
                <span className={styles.typingDot} />
                <span className={styles.typingDot} />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </>
      )}
    </div>
  );
};

export default CustomMessageList;
